import React from 'react';
import Input from './Input';
import Button from './Button';
import { useLanguage } from '../../context/LanguageContext';

export interface Filters {
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  bathrooms: string;
  type: string;
  city: string;
  zipCode: string;
  minArea: string;
  maxArea: string;
}

interface FilterSidebarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onClose?: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onChange, onClose }) => {
  const { t } = useLanguage();
  const hasFilters = Object.values(filters).some((value) => value !== '');

  const clearFilters = () => {
    onChange({
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      bathrooms: '',
      type: '',
      city: '',
      zipCode: '',
      minArea: '',
      maxArea: '',
    });
  };

  const handleChange = (key: keyof Filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onChange({ ...filters, [key]: e.target.value });
  };

  return (
    <div className="p-6 w-80 bg-white border-r border-gray-200 shadow-sm sm:w-full sm:border-0 sm:p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t('common.filters')}</h2>
        <div className="flex items-center gap-2">
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {t('common.clear')}
            </button>
          )}
          {onClose && (
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full p-1 sm:hidden"
              aria-label={t('common.close')}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div className="space-y-5">
        <div className="bg-neutral-50 rounded-2xl p-4 border border-neutral-200">
          <Input
            id="city"
            label={t('common.location')}
            value={filters.city}
            onChange={handleChange('city')}
            placeholder={t('common.enterLocation')}
            aria-label={t('common.location')}
          />
          <div className="mt-4">
            <Input
              id="zipCode"
              label={t('common.postalCode')}
              value={filters.zipCode}
              onChange={handleChange('zipCode')}
              placeholder={t('common.enterPostalCode')}
              aria-label={t('common.postalCode')}
            />
          </div>
        </div>
        
        <div className="bg-neutral-50 rounded-2xl p-4 border border-neutral-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('common.price')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              id="minPrice"
              type="number"
              label={t('common.minPrice')}
              value={filters.minPrice}
              onChange={handleChange('minPrice')}
              placeholder={t('common.minPrice')}
              aria-label={t('common.minPrice')}
            />
            <Input
              id="maxPrice"
              type="number"
              label={t('common.maxPrice')}
              value={filters.maxPrice}
              onChange={handleChange('maxPrice')}
              placeholder={t('common.maxPrice')}
              aria-label={t('common.maxPrice')}
            />
          </div>
        </div>

        <div className="bg-neutral-50 rounded-2xl p-4 border border-neutral-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('common.rooms')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              id="bedrooms"
              type="number"
              label={t('common.bedrooms')}
              value={filters.bedrooms}
              onChange={handleChange('bedrooms')}
              placeholder={t('common.bedrooms')}
              aria-label={t('common.bedrooms')}
            />
            <Input
              id="bathrooms"
              type="number"
              label={t('common.bathrooms')}
              value={filters.bathrooms}
              onChange={handleChange('bathrooms')}
              placeholder={t('common.bathrooms')}
              aria-label={t('common.bathrooms')}
            />
          </div>
        </div>

        <div className="bg-neutral-50 rounded-2xl p-4 border border-neutral-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('common.area')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              id="minArea"
              type="number"
              label={t('common.minArea')}
              value={filters.minArea}
              onChange={handleChange('minArea')}
              placeholder={t('common.minArea')}
              aria-label={t('common.minArea')}
            />
            <Input
              id="maxArea"
              type="number"
              label={t('common.maxArea')}
              value={filters.maxArea}
              onChange={handleChange('maxArea')}
              placeholder={t('common.maxArea')}
              aria-label={t('common.maxArea')}
            />
          </div>
        </div>

        <div className="bg-neutral-50 rounded-2xl p-4 border border-neutral-200">
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-3">
            {t('common.type')}
          </label>
          <select
            id="type"
            value={filters.type}
            onChange={handleChange('type')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            aria-label={t('common.type')}
          >
            <option value="">{t('common.allTypes')}</option>
            <option value="apartment">{t('property.type.apartment')}</option>
            <option value="house">{t('property.type.house')}</option>
            <option value="condo">{t('property.type.condo')}</option>
            <option value="land">{t('property.type.land')}</option>
            <option value="commercial">{t('property.type.commercial')}</option>
          </select>
        </div>

        {onClose && (
          <Button 
            fullWidth 
            onClick={onClose} 
            className="sm:hidden mt-2"
            aria-label={t('common.applyFilters')}
          >
            {t('common.applyFilters')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilterSidebar;
